import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type { Printer, PrinterInsert, PrinterUpdate } from '../lib/database.types'

const PRINTERS_KEY = ['printers']

export function usePrinters() {
  return useQuery({
    queryKey: PRINTERS_KEY,
    queryFn: async (): Promise<Printer[]> => {
      const { data, error } = await supabase
        .from('printers')
        .select('*')
        .order('created_at', { ascending: true })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useCreatePrinter() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Omit<PrinterInsert, 'user_id'>): Promise<Printer> => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')
      const { data, error } = await supabase
        .from('printers')
        .insert({ ...input, user_id: user.id })
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRINTERS_KEY })
    },
  })
}

export function useUpdatePrinter() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & PrinterUpdate): Promise<Printer> => {
      const { data, error } = await supabase
        .from('printers')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRINTERS_KEY })
    },
  })
}

export function useDeletePrinter() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('printers').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: PRINTERS_KEY })
    },
  })
}
